import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, QrCode, CheckCircle2, ShieldCheck, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/ist";

/**
 * Public, no-auth invoice payment page.
 * Reached from the "Pay now" button in invoice emails. URL: /pay/:invoiceId?t=<sig>
 * Backend endpoints:
 *   GET  /api/public/payments/:id              (validate signature, return invoice)
 *   POST /api/public/payments/:id/qr           (create UPI QR)
 *   GET  /api/public/payments/:id/qr/status    (poll)
 */
export default function PayInvoice() {
  const { invoiceId } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t");

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [qr, setQr] = useState(null);
  const [paid, setPaid] = useState(false);
  const [creating, setCreating] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/public/payments/${invoiceId}`, { params: { t } })
      .then(r => { setInvoice(r.data.invoice); setCustomer(r.data.customer); setPaid(r.data.invoice.status === "paid"); })
      .catch(e => setErr(e?.response?.data?.detail || "Invalid or expired payment link"))
      .finally(() => setLoading(false));
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [invoiceId, t]);

  const startPolling = () => {
    if (timerRef.current) return;
    timerRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/public/payments/${invoiceId}/qr/status`, { params: { t } });
        if (data.paid) { setPaid(true); clearInterval(timerRef.current); timerRef.current = null; }
      } catch { /* keep polling */ }
    }, 3000);
  };

  const createQr = async () => {
    setCreating(true);
    try {
      const { data } = await api.post(`/public/payments/${invoiceId}/qr`, null, { params: { t } });
      setQr(data); startPolling();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create QR");
    } finally { setCreating(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{background:'#0a0f1f'}}><Loader2 className="animate-spin text-blue-400"/></div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center px-5" style={{background:'#0a0f1f'}}>
      <div className="card p-8 max-w-md text-center" data-testid="pay-error">
        <AlertTriangle className="mx-auto text-amber-400 mb-2" size={36}/>
        <h1 className="text-white text-xl font-bold" style={{fontFamily:'Outfit'}}>Link not valid</h1>
        <p className="text-slate-400 mt-2 text-sm">{err}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen px-5 py-10" style={{background:'#0a0f1f'}}>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-block text-2xl font-extrabold tracking-tight" style={{fontFamily:'Outfit'}}>
            <span style={{color: 'var(--brand-blue)'}}>Optivize</span><span className="text-white">.digital</span>
          </div>
        </div>

        <div className="card p-6 mb-4" data-testid="invoice-summary">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-slate-400 text-xs">Invoice</div>
              <div className="text-white font-bold text-lg">{invoice.number}</div>
              {invoice.issued_at && <div className="text-slate-500 text-xs mt-1">Issued {fmtDate(invoice.issued_at)}</div>}
            </div>
            <div className="text-right">
              <div className="text-slate-400 text-xs">Amount due</div>
              <div className="text-white font-extrabold text-3xl" style={{fontFamily:'Outfit'}}>₹{Number(invoice.amount).toLocaleString()}</div>
            </div>
          </div>
          {customer?.email && (
            <div className="text-xs text-slate-500 mt-3">
              Billed to <span className="text-slate-300">{customer.name}</span> · {customer.email}
            </div>
          )}
        </div>

        {paid ? (
          <div className="card p-8 text-center" data-testid="paid-state">
            <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-3"/>
            <h2 className="text-white text-2xl font-bold" style={{fontFamily:'Outfit'}}>Payment received</h2>
            <p className="text-slate-400 mt-1 text-sm">Thank you! A receipt has been emailed to <strong className="text-cyan-300">{customer?.email}</strong>.</p>
          </div>
        ) : !qr ? (
          <div className="card p-6 text-center">
            <QrCode size={36} className="mx-auto text-cyan-300 mb-3"/>
            <div className="text-white font-semibold mb-1">Pay via UPI QR</div>
            <p className="text-slate-400 text-sm mb-4">Scan with any UPI app (GPay, PhonePe, Paytm, BHIM).<br/>Payment is processed by <strong className="text-white">Razorpay</strong>.</p>
            <button onClick={createQr} disabled={creating} className="btn-primary inline-flex items-center gap-2" data-testid="pay-qr-btn">
              {creating ? <Loader2 size={16} className="animate-spin"/> : <QrCode size={16}/>}
              Generate UPI QR for ₹{Number(invoice.amount).toLocaleString()}
            </button>
          </div>
        ) : (
          <div className="card p-6 flex flex-col sm:flex-row gap-5 items-start" data-testid="pay-qr-display">
            <div className="bg-white p-3 rounded-xl shrink-0 self-center sm:self-start">
              <img src={qr.image_url} alt="UPI QR" className="w-56 h-56" data-testid="pay-qr-image"/>
            </div>
            <div className="flex-1">
              <div className="text-white font-semibold flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-300"/> Waiting for your payment…</div>
              <p className="text-slate-400 text-sm mt-2">Open any UPI app, tap "Scan QR" and complete the ₹{Number(invoice.amount).toLocaleString()} payment. This page refreshes automatically.</p>
              <p className="text-xs text-slate-500 mt-3">Powered by Razorpay UPI · Optivize Digital</p>
            </div>
          </div>
        )}

        <div className="mt-6 text-center text-xs text-slate-500">
          Need help? Email <a className="text-cyan-400" href="mailto:sales@optivize.digital">sales@optivize.digital</a> or call +91 81006 71006.
        </div>
      </div>
    </div>
  );
}
